<script setup lang="ts">
import type { TextMediaBlockV2 } from '~/types/storyblok/text-media-block-v2'

import type { trackBlockClickEmit } from '~/types/track-block-click'
import { useImage } from '~/composables/useImage'
import { getEdgeClasses } from '~/lib/getEdgeClasses'
import { normalizeTextMediaBlockV2 } from '~/types/storyblok/text-media-block-v2'
import ButtonIcon from '~/components/button/ButtonIcon.vue'
import ImageResponsive from '~/components/image/Responsive.vue'
import { useRichtext } from '~/composables/useRichtext'

interface Props {
  blok: TextMediaBlockV2
}

const props = defineProps<Props>()
const emit = defineEmits<trackBlockClickEmit>()

const blok = toRef(props, 'blok')
const textMediaBlock = computed(() => normalizeTextMediaBlockV2(blok.value))
const bgColor = computed(() => textMediaBlock.value.backgroundColor)
const textColor = computed(() => textMediaBlock.value.fontAndCtaColor)

const normalizedText = useRichtext(computed(() => textMediaBlock.value.text))

const { imageMobile, imageAlt } = useImage(
  textMediaBlock.value,
)

function trackBlockClick(url: string) {
  emit('trackBlockClick', url, 'button')
}
</script>

<template>
  <section
    v-editable="blok"
    :class="[
      { 'media--left': textMediaBlock.isMediaLeft },
      { 'media--right': textMediaBlock.isMediaRight },
      { 'media--top': textMediaBlock.isMediaTop },
      {
        'only-title':
          !textMediaBlock.ctaLabel
          && !textMediaBlock.ctaLink
          && !textMediaBlock.label
          && !textMediaBlock.link
          && !textMediaBlock.text?.content?.find(el => el.content),
      },
      {
        'without-text': !textMediaBlock.text?.content?.find(el => el.content),
      },
      getEdgeClasses(textMediaBlock.edgeStyle),
    ]"
  >
    <NuxtLink
      v-if="textMediaBlock.largeAsset"
      class="media__container"
      :to="$url(textMediaBlock.ctaLink ?? '')"
      :target="$url(textMediaBlock.ctaLink ?? '').startsWith('http') ? '_blank' : '_self'"
    >
      <!--
        Sets the aspect ratio for the media element:
        - Uses 16:9 when media is positioned at the top (isMediaTop is true)
        - Falls back to the specified imageRatio property if available
        - Defaults to 'auto' if neither condition is met
      -->
      <ImageResponsive
        provider="storyblok"
        class="img"
        :src="imageMobile ?? ''"
        :alt="imageAlt ?? ''"
        :large-asset="textMediaBlock.largeAsset"
        :ratio="textMediaBlock.isMediaTop ? '16:9' : textMediaBlock.imageRatio || 'auto'"
        mobile-ratio="4:3"
        :lazy="true"
        :object-fit="textMediaBlock.imageRatio ? 'contain' : 'cover'"
      />
      <img
        v-if="textMediaBlock.overlay"
        class="media__overlay"
        :src="textMediaBlock.overlay"
        :alt="textMediaBlock.altTextOverlay ?? ''"
      >
    </NuxtLink>

    <div
      class="content"
      :class="[getEdgeClasses(textMediaBlock.edgeStyle)]"
    >
      <div class="content--left">
        <h2
          v-if="textMediaBlock.shortTitle"
          class="content__title content__title--short"
        >
          {{ textMediaBlock.shortTitle }}
        </h2>
        <h2
          v-if="textMediaBlock.longTitle"
          class="content__title content__title--long"
        >
          {{ textMediaBlock.longTitle }}
        </h2>
      </div>
      <div class="content--right">
        <div
          v-if="textMediaBlock.text"
          class="content__text"
          v-html="normalizedText"
        />
        <ButtonIcon
          v-if="textMediaBlock.ctaLink && textMediaBlock.ctaLabel"
          class="btn"
          :text="$t(textMediaBlock.ctaLabel)"
          left-icon="bullet"
          right-icon="forward"
          :blinking="true"
          :background-color="textMediaBlock.fontAndCtaColor"
          :text-color="textMediaBlock.backgroundColor"
          data-test="cta-internal-link-btn"
          tag="nuxt-link"
          :target="textMediaBlock.ctaLink.startsWith('http') ? '_blank' : '_self'"
          :to="$url(textMediaBlock.ctaLink ?? '')"
          @click="trackBlockClick($url(textMediaBlock.ctaLink || '')) "
        />
        <NuxtLink
          v-if="textMediaBlock.link && textMediaBlock.label"
          class="text-block__link"
          :to="$url(textMediaBlock.link)"
          :target="$url(textMediaBlock.link).startsWith('http') ? '_blank' : '_self'"
        >
          {{ textMediaBlock.label }}
        </NuxtLink>
      </div>
    </div>
  </section>
</template>

<style scoped>
.content {
  display: flex;
  background-color: v-bind(bgColor);
  color: v-bind(textColor);
  padding: 2.4rem 2.4rem 5.6rem 2.4rem;
  @media (max-width: 959px) {
    padding: 2.8rem 1.6rem 4rem 1.6rem;
    flex-direction: column;
  }
}

.content--left {
  width: 100%;
  margin-bottom: 1.6rem;

  @media (min-width: 960px) {
    margin-right: 4.8rem;
    width: 50%;
  }
}

.content--right {
  @media (min-width: 960px) {
    width: 50%;
  }
}

.content__title--short {
  font: var(--font-h3);
  color: v-bind(textColor);

  @media (max-width: 959px) {
    font: var(--font-h4);
    color: v-bind(textColor);
  }
}

.content__title--long {
  font: var(--font-h5);
  color: v-bind(textColor);

  @media (max-width: 959px) {
    font: var(--font-h6);
    color: v-bind(textColor);
  }
}

.content__text {
  font: var(--font-body1);
  color: v-bind(textColor);
}

.media__container {
  position: relative;
  img {
    width: 100%;
  }
}

.media__overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 8rem;
  @media (min-width: 960px) {
    height: 14rem;
  }
}

.media--right, .media--left {
  display: flex;
  flex-direction: column;

  .media__container {
    width: 100%;

    @media (min-width: 960px) {
      width: 50%;
    }
  }

  .content {
    flex-direction: column;
    justify-content: flex-end;
    width: 100%;

    @media (min-width: 960px) {
      width: 50%;
      padding-top: 28rem;
    }
  }

  .content--left {
    width: 100%;
  }

  .content--right {
    width: 100%;
    @media (min-width: 960px) {
      width: 80%;
    }
  }
}

.media--right {
  @media (min-width: 960px) {
    flex-direction: row-reverse;
  }
}

.media--left {
  @media (min-width: 960px) {
    flex-direction: row;
  }
}

.media--top {
  .media__container {
    width: 100%;
    img {
      width: 100%;
    }
  }
}

.without-text {
  .content--left {
    width: 100%;
  }
}

.only-title {
  .content {
    padding-left: 2.4rem;
  }
  .content--left {
    width: 75%;
    padding-left: 0;
  }
}

.text-block__link {
  text-decoration: underline;
  display: block;
  margin-top: 2.4rem;
}

.btn {
  margin-top: 4rem;
}

.teeth {
    --size: 4px;
  --R: calc(var(--size) * 1.28);
  --mask: radial-gradient(var(--R) at 50% calc(1.8 * var(--size)), #000 99%, #0000 101%) calc(50% - 2 * var(--size)) 0 / calc(4 * var(--size)) 100%, radial-gradient(var(--R) at 50% calc(-0.8 * var(--size)), #0000 99%, #000 101%) 50% var(--size) / calc(4 * var(--size)) 100% repeat-x;
  -webkit-mask: var(--mask);
  mask: var(--mask);
  margin-top: -1.2rem;
}

.scallop {
    --mask:
    radial-gradient(11.89px at 50% 16.80px,#000 99%,#0000 101%) calc(50% - 16px) 0/32px 100%, radial-gradient(11.89px at 50% -8.8px,#0000 99%,#000 101%) 50% 8px/32px 100% repeat-x;
  -webkit-mask: var(--mask);
  mask: var(--mask);
  margin-top: -1.2rem;
}

.wave {
    --mask: radial-gradient(10px at top, #0000 90%, #000) 50% / 18px calc(100% + 10px);
  -webkit-mask: var(--mask);
  mask: var(--mask);
  margin-top: -1.2rem;
}
</style>
